/**
 * ConfiguracoesPage - Settings page
 * Refactored to use extracted hooks and components
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Shield, Database, FileText, HardDrive } from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import useAuthStore from '../stores/authStore';
import { ROLES } from '../constants/roles';

// Hooks
import { useProfileForm } from '../hooks/useProfileForm';
import { usePasswordChange } from '../hooks/usePasswordChange';
import { useLogoUpload } from '../hooks/useLogoUpload';
import { useMotivoResponsavel } from '../hooks/useMotivoResponsavel';
import { useClassificacaoDocumento } from '../hooks/useClassificacaoDocumento';

// Components
import {
  PerfilSection,
  SegurancaSection,
  SistemaSection,
  MotivoSection,
  ClassificacaoDocumentoSection,
  ArmazenamentoSection,
  SMTPInfoModal,
} from '../components/configuracoes';

type ConfigSection = 'perfil' | 'notificacoes' | 'seguranca' | 'sistema' | 'motivo' | 'classificacao-documento' | 'armazenamento';

export const ConfiguracoesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSection = (searchParams.get('section') as ConfigSection) || 'perfil';
  const [activeSection, setActiveSection] = useState<ConfigSection>(initialSection);
  const [showSmtpInfoModal, setShowSmtpInfoModal] = useState(false);

  const { user } = useAuthStore();
  const isAdmin = user?.role === ROLES.MASTER;

  // Profile hook
  const {
    formData,
    isLoadingProfile,
    isSavingProfile,
    profileError,
    profileSuccess,
    handleProfileInputChange,
    handleSaveProfile,
    getUserInitials,
  } = useProfileForm();

  // Password hook
  const {
    passwordData,
    passwordStrength,
    passwordErrors,
    isChangingPassword,
    passwordMessage,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    setShowCurrentPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    handlePasswordChange,
    handleChangePassword,
    getStrengthColor,
  } = usePasswordChange();

  // Logo hook
  const {
    logoPreview,
    currentLogo,
    isUploadingLogo,
    fileInputRef,
    handleLogoFileChange,
    handleDeleteLogo,
    miniLogoPreview,
    currentMiniLogo,
    isUploadingMiniLogo,
    miniLogoFileInputRef,
    handleMiniLogoFileChange,
    handleDeleteMiniLogo,
    ConfirmDialog: LogoConfirmDialog,
  } = useLogoUpload();

  // Motivo hook
  const {
    motivos,
    isLoadingMotivos,
    isSavingMotivo,
    editingMotivo,
    newMotivo,
    editMotivoData,
    setNewMotivo,
    setEditMotivoData,
    handleCreateMotivo,
    handleUpdateMotivo,
    handleDeleteMotivo,
    startEditing,
    cancelEditing,
    ConfirmDialog: MotivoConfirmDialog,
  } = useMotivoResponsavel(activeSection);

  // Classificacao Documento hook
  const {
    classificacoes,
    isLoadingClassificacoes,
    isSavingClassificacao,
    editingClassificacao,
    newClassificacao,
    editClassificacaoData,
    setNewClassificacao,
    setEditClassificacaoData,
    handleCreateClassificacao,
    handleUpdateClassificacao,
    handleDeleteClassificacao,
    startEditing: startEditingClassificacao,
    cancelEditing: cancelEditingClassificacao,
    ConfirmDialog: ClassificacaoConfirmDialog,
  } = useClassificacaoDocumento(activeSection);

  // Sections configuration
  const sections = [
    { id: 'perfil', label: 'Perfil', icon: User },
    { id: 'seguranca', label: isAdmin ? 'Segurança' : 'Segurança Pessoal', icon: Shield },
    ...(isAdmin ? [
      { id: 'sistema', label: 'Personalização', icon: Database },
      { id: 'motivo', label: 'Motivo/Responsável', icon: FileText },
      { id: 'classificacao-documento', label: 'Classificação Documento', icon: FileText },
      { id: 'armazenamento', label: 'Configuração Armazenamento', icon: HardDrive }
    ] : [])
  ];

  // Sync activeSection with URL params
  useEffect(() => {
    const section = searchParams.get('section') as ConfigSection;
    if (section && ['perfil', 'notificacoes', 'seguranca', 'sistema', 'motivo', 'classificacao-documento', 'armazenamento'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Internal sidebar is now hidden for all sections
  const showInternalSidebar = !['sistema', 'perfil', 'notificacoes', 'seguranca', 'motivo', 'classificacao-documento', 'armazenamento'].includes(activeSection);

  return (
    <>
      <MainLayout>
        <LogoConfirmDialog />
        <MotivoConfirmDialog />
        <ClassificacaoConfirmDialog />
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar de Navegação */}
            {showInternalSidebar && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 sticky top-8">
                  <nav className="space-y-2">
                    {sections.filter(section => {
                      const systemSections = ['sistema'];
                      const isSystemActive = systemSections.includes(activeSection);
                      const isSystemSection = systemSections.includes(section.id);
                      return isSystemActive ? isSystemSection : !isSystemSection;
                    }).map((section) => {
                      const Icon = section.icon;
                      const isActive = activeSection === section.id;

                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            setActiveSection(section.id as ConfigSection);
                            setSearchParams({ section: section.id });
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{section.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            )}

            {/* Conteúdo */}
            <div className={showInternalSidebar ? "lg:col-span-3" : "lg:col-span-4"}>
              {activeSection === 'perfil' && (
                <PerfilSection
                  formData={formData}
                  isLoadingProfile={isLoadingProfile}
                  isSavingProfile={isSavingProfile}
                  profileError={profileError}
                  profileSuccess={profileSuccess}
                  onInputChange={handleProfileInputChange}
                  onSave={handleSaveProfile}
                  getUserInitials={getUserInitials}
                />
              )}

              {activeSection === 'seguranca' && (
                <SegurancaSection
                  passwordData={passwordData}
                  passwordStrength={passwordStrength}
                  passwordErrors={passwordErrors}
                  isChangingPassword={isChangingPassword}
                  passwordMessage={passwordMessage}
                  showCurrentPassword={showCurrentPassword}
                  showNewPassword={showNewPassword}
                  showConfirmPassword={showConfirmPassword}
                  setShowCurrentPassword={setShowCurrentPassword}
                  setShowNewPassword={setShowNewPassword}
                  setShowConfirmPassword={setShowConfirmPassword}
                  onPasswordChange={handlePasswordChange}
                  onSubmit={handleChangePassword}
                  getStrengthColor={getStrengthColor}
                />
              )}

              {activeSection === 'sistema' && isAdmin && (
                <SistemaSection
                  logoPreview={logoPreview}
                  currentLogo={currentLogo}
                  isUploadingLogo={isUploadingLogo}
                  fileInputRef={fileInputRef}
                  onLogoFileChange={handleLogoFileChange}
                  onDeleteLogo={handleDeleteLogo}
                  miniLogoPreview={miniLogoPreview}
                  currentMiniLogo={currentMiniLogo}
                  isUploadingMiniLogo={isUploadingMiniLogo}
                  miniLogoFileInputRef={miniLogoFileInputRef}
                  onMiniLogoFileChange={handleMiniLogoFileChange}
                  onDeleteMiniLogo={handleDeleteMiniLogo}
                />
              )}

              {activeSection === 'armazenamento' && isAdmin && (
                <ArmazenamentoSection />
              )}

              {activeSection === 'motivo' && isAdmin && (
                <MotivoSection
                  motivos={motivos}
                  isLoadingMotivos={isLoadingMotivos}
                  isSavingMotivo={isSavingMotivo}
                  editingMotivo={editingMotivo}
                  newMotivo={newMotivo}
                  editMotivoData={editMotivoData}
                  setNewMotivo={setNewMotivo}
                  setEditMotivoData={setEditMotivoData}
                  onCreateMotivo={handleCreateMotivo}
                  onUpdateMotivo={handleUpdateMotivo}
                  onDeleteMotivo={handleDeleteMotivo}
                  onStartEditing={startEditing}
                  onCancelEditing={cancelEditing}
                />
              )}

              {activeSection === 'classificacao-documento' && isAdmin && (
                <ClassificacaoDocumentoSection
                  classificacoes={classificacoes}
                  isLoadingClassificacoes={isLoadingClassificacoes}
                  isSavingClassificacao={isSavingClassificacao}
                  editingClassificacao={editingClassificacao}
                  newClassificacao={newClassificacao}
                  editClassificacaoData={editClassificacaoData}
                  setNewClassificacao={setNewClassificacao}
                  setEditClassificacaoData={setEditClassificacaoData}
                  onCreateClassificacao={handleCreateClassificacao}
                  onUpdateClassificacao={handleUpdateClassificacao}
                  onDeleteClassificacao={handleDeleteClassificacao}
                  onStartEditing={startEditingClassificacao}
                  onCancelEditing={cancelEditingClassificacao}
                />
              )}
            </div>
          </div>
        </div>
      </MainLayout>

      {/* Modal Informativo - Configuração SMTP */}
      <SMTPInfoModal
        isOpen={showSmtpInfoModal}
        onClose={() => setShowSmtpInfoModal(false)}
      />
    </>
  );
};

export default ConfiguracoesPage;
